import { useState } from 'react';
import type {
  ShakedownResult,
  GearMatch,
  ShakedownOptimization,
  MissingEssential,
  OptimizationSuggestion,
  ShakedownProduct,
} from '../lib/api';
import { getProductImageUrl } from '../lib/api';
import { ShareButtons } from './ShareButtons';

interface ShakedownResultsProps {
  result: ShakedownResult;
  onSave: () => void;
  onReset: () => void;
  isSaving: boolean;
  savedUrl?: string;
}

function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)} kg`;
  }
  return `${grams} g`;
}

function formatWeightDiff(grams: number): string {
  const prefix = grams > 0 ? '+' : '';
  if (Math.abs(grams) >= 1000) {
    return `${prefix}${(grams / 1000).toFixed(1)}kg`;
  }
  return `${prefix}${grams}g`;
}

function getValueRatingColor(rating: OptimizationSuggestion['value_rating']): string {
  switch (rating) {
    case 'excellent':
      return 'text-green-400 bg-green-500/10';
    case 'good':
      return 'text-cyan-400 bg-cyan-500/10';
    case 'moderate':
      return 'text-yellow-400 bg-yellow-500/10';
    case 'poor':
      return 'text-red-400 bg-red-500/10';
    default:
      return 'text-neutral-400 bg-neutral-500/10';
  }
}

function getImportanceColor(importance: MissingEssential['importance']): string {
  switch (importance) {
    case 'critical':
      return 'text-red-400 bg-red-500/10';
    case 'recommended':
      return 'text-yellow-400 bg-yellow-500/10';
    case 'optional':
      return 'text-neutral-400 bg-neutral-500/10';
    default:
      return 'text-neutral-400 bg-neutral-500/10';
  }
}

function formatCategory(categoryId: string): string {
  const categoryNames: Record<string, string> = {
    shelter: 'Shelter',
    sleeping_bag: 'Sleeping Bag',
    sleeping_pad: 'Sleeping Pad',
    backpack: 'Backpack',
    cooking: 'Cooking',
    water: 'Water Treatment',
    rain_jacket: 'Rain Jacket',
    insulation: 'Insulation',
    footwear: 'Footwear',
    lighting: 'Lighting',
    navigation: 'Navigation',
    trekking_poles: 'Trekking Poles',
  };
  return categoryNames[categoryId] || categoryId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// Smaller image components for compact shakedown cards
function SmallProductImagePlaceholder() {
  return (
    <div className="w-12 h-12 flex items-center justify-center bg-neutral-800 rounded shrink-0">
      <svg
        className="w-5 h-5 text-neutral-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        />
      </svg>
    </div>
  );
}

interface SmallProductImageProps {
  src: string;
  alt: string;
  onError: () => void;
}

function SmallProductImage({ src, alt, onError }: SmallProductImageProps) {
  return (
    <img
      src={src}
      alt={alt}
      onError={onError}
      className="w-12 h-12 object-cover rounded shrink-0 bg-neutral-800"
    />
  );
}

// Component to render product with image
function ProductWithImage({ product }: { product: ShakedownProduct }) {
  const [imageError, setImageError] = useState(false);
  const imageUrl = getProductImageUrl(product.cf_image_id, product.image_url, 'thumbnail');
  const hasValidImage = imageUrl && !imageError;
  const imageAlt = product.image_alt || product.name;

  return (
    <>
      {hasValidImage ? (
        <SmallProductImage
          src={imageUrl}
          alt={imageAlt}
          onError={() => setImageError(true)}
        />
      ) : (
        <SmallProductImagePlaceholder />
      )}
    </>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = true,
  children,
  badge,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between px-4 py-3 bg-neutral-900/50 hover:bg-neutral-900 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-white">{title}</h3>
          {badge}
        </div>
        <svg
          className={`w-5 h-5 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        className={`transition-all duration-200 ${isOpen ? 'block' : 'hidden'}`}
        style={{ visibility: isOpen ? 'visible' : 'hidden' }}
      >
        <div className="p-4 border-t border-neutral-800">{children}</div>
      </div>
    </div>
  );
}

function SuggestedAlternativeItem({ alt }: { alt: ShakedownProduct }) {
  const [imageError, setImageError] = useState(false);
  const imageUrl = getProductImageUrl(alt.cf_image_id, alt.image_url, 'thumbnail');
  const hasValidImage = imageUrl && !imageError;
  const imageAlt = alt.image_alt || alt.name;

  return (
    <div className="flex items-center gap-2 text-sm">
      {hasValidImage ? (
        <img
          src={imageUrl}
          alt={imageAlt}
          onError={() => setImageError(true)}
          className="w-8 h-8 object-cover rounded shrink-0 bg-neutral-800"
        />
      ) : (
        <div className="w-8 h-8 flex items-center justify-center bg-neutral-800 rounded shrink-0">
          <svg className="w-4 h-4 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
      )}
      <span className="text-neutral-300 truncate flex-1">{alt.name}</span>
      <span className="text-neutral-500 shrink-0">
        {formatWeight(alt.weight_g)} · £{alt.price_gbp}
      </span>
    </div>
  );
}

function MatchedItemCard({ match }: { match: GearMatch }) {
  const { parsed_item, matched_product, confidence, weight_difference_g, inferred_category, suggested_alternatives } = match;

  if (!matched_product) {
    // Unmatched item - show with category detection and alternatives
    if (inferred_category && suggested_alternatives && suggested_alternatives.length > 0) {
      return (
        <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
          <div className="flex justify-between items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-amber-200">{parsed_item.name}</p>
              {parsed_item.weight_g && (
                <p className="text-sm text-neutral-500">{formatWeight(parsed_item.weight_g)}</p>
              )}
              <p className="text-xs text-amber-400/80 mt-1">
                Detected as: {formatCategory(inferred_category)}
              </p>
            </div>
            <span className="px-2 py-0.5 text-xs rounded bg-amber-500/10 text-amber-400 shrink-0">
              No exact match
            </span>
          </div>
          <div className="mt-3 pt-3 border-t border-amber-500/10">
            <p className="text-xs text-neutral-400 mb-2">Similar items we recommend:</p>
            <div className="space-y-2">
              {suggested_alternatives.map((alt) => (
                <SuggestedAlternativeItem key={alt.id} alt={alt} />
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Unmatched with no category detection
    return (
      <div className="p-3 bg-neutral-900/30 border border-neutral-800 rounded-lg">
        <div className="flex justify-between items-start">
          <div>
            <p className="font-medium text-neutral-300">{parsed_item.name}</p>
            {parsed_item.weight_g && (
              <p className="text-sm text-neutral-500">{formatWeight(parsed_item.weight_g)}</p>
            )}
          </div>
          <span className="px-2 py-0.5 text-xs rounded bg-neutral-700 text-neutral-400">
            Not matched
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 bg-neutral-900/30 border border-neutral-800 rounded-lg">
      <div className="flex items-start gap-3">
        {/* Product Image */}
        <ProductWithImage product={matched_product} />

        <div className="flex justify-between items-start gap-3 flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate">{matched_product.name}</p>
            <p className="text-sm text-neutral-500">
              {formatWeight(matched_product.weight_g)} · £{matched_product.price_gbp}
            </p>
            <p className="text-xs text-neutral-400 mt-1">
              Matched from: <span className="text-neutral-300">{parsed_item.name}</span>
            </p>
          </div>
          <div className="text-right shrink-0">
            <span className="px-2 py-0.5 text-xs rounded bg-green-500/10 text-green-400">
              {Math.round(confidence * 100)}%
            </span>
            {weight_difference_g !== undefined && weight_difference_g !== 0 && (
              <p className={`text-xs mt-1 ${weight_difference_g > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {formatWeightDiff(weight_difference_g)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OptimizationSuggestionItem({ suggestion }: { suggestion: OptimizationSuggestion }) {
  const [imageError, setImageError] = useState(false);
  const imageUrl = getProductImageUrl(suggestion.product.cf_image_id, suggestion.product.image_url, 'thumbnail');
  const hasValidImage = imageUrl && !imageError;
  const imageAlt = suggestion.product.image_alt || suggestion.product.name;

  return (
    <div className="flex items-center gap-3 p-2 bg-neutral-800/50 rounded">
      {/* Product Image */}
      {hasValidImage ? (
        <img
          src={imageUrl}
          alt={imageAlt}
          onError={() => setImageError(true)}
          className="w-10 h-10 object-cover rounded shrink-0 bg-neutral-800"
        />
      ) : (
        <div className="w-10 h-10 flex items-center justify-center bg-neutral-800 rounded shrink-0">
          <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{suggestion.product.name}</p>
        <p className="text-xs text-neutral-500">
          {formatWeight(suggestion.product.weight_g)} · £{suggestion.product.price_gbp}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-mono text-green-400">-{suggestion.weight_saving_g}g</p>
        <p className={`text-xs ${suggestion.price_difference <= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {suggestion.price_difference <= 0 ? '-' : '+'}£{Math.abs(suggestion.price_difference)}
        </p>
        <span className={`inline-block mt-1 px-1.5 py-0.5 text-xs rounded ${getValueRatingColor(suggestion.value_rating)}`}>
          {suggestion.value_rating}
        </span>
      </div>
    </div>
  );
}

function OptimizationCard({ optimization }: { optimization: ShakedownOptimization }) {
  const { current_item, current_product, suggestions } = optimization;

  if (suggestions.length === 0) return null;

  return (
    <div className="p-4 bg-neutral-900/30 border border-neutral-800 rounded-lg space-y-3">
      <div className="border-b border-neutral-800 pb-3">
        <p className="text-sm text-neutral-400">Current item</p>
        <div className="flex items-center gap-3 mt-1">
          {current_product && <ProductWithImage product={current_product} />}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate">
              {current_product?.name || current_item.name}
            </p>
            {current_product && (
              <p className="text-sm text-neutral-500">
                {formatWeight(current_product.weight_g)} · £{current_product.price_gbp}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm text-neutral-400">Lighter alternatives</p>
        {suggestions.map((suggestion) => (
          <OptimizationSuggestionItem key={suggestion.product.id} suggestion={suggestion} />
        ))}
      </div>
    </div>
  );
}

function MissingEssentialCard({ essential }: { essential: MissingEssential }) {
  return (
    <div className="p-3 bg-neutral-900/30 border border-neutral-800 rounded-lg">
      <div className="flex justify-between items-start gap-3">
        <div>
          <p className="font-medium text-white">{essential.category_name}</p>
          <p className="text-sm text-neutral-400 mt-1">{essential.reason}</p>
        </div>
        <span className={`px-2 py-0.5 text-xs rounded shrink-0 ${getImportanceColor(essential.importance)}`}>
          {essential.importance}
        </span>
      </div>
    </div>
  );
}

export function ShakedownResults({
  result,
  onSave,
  onReset,
  isSaving,
  savedUrl,
}: ShakedownResultsProps) {
  const { totals, matches, optimizations, missing_essentials } = result;

  const matchedCount = matches.filter((m) => m.matched_product).length;

  // Build share description
  const shareDescription = `Total weight: ${formatWeight(totals.total_weight_g)} | ${totals.item_count} items | ${optimizations.length} optimizations`;

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      {/* Summary Card */}
      <div className="bg-gradient-to-r from-neutral-900 to-neutral-900/50 border border-neutral-800 rounded-xl p-6">
        <h2 className="font-mono text-xl font-bold text-white mb-4">Gear Analysis</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-neutral-500">Total Weight</p>
            <p className="text-2xl font-bold text-orange-500">{formatWeight(totals.total_weight_g)}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-500">Items</p>
            <p className="text-2xl font-bold text-white">{totals.item_count} items</p>
          </div>
          <div>
            <p className="text-sm text-neutral-500">Matched</p>
            <p className="text-2xl font-bold text-white">
              {matchedCount} matched
            </p>
          </div>
          <div>
            <p className="text-sm text-neutral-500">Match Rate</p>
            <p className="text-2xl font-bold text-white">{Math.round(totals.match_rate * 100)}%</p>
          </div>
        </div>
      </div>

      {/* Matched Items */}
      <CollapsibleSection
        title="Matched Items"
        badge={
          <span className="px-2 py-0.5 text-xs rounded bg-neutral-700 text-neutral-300">
            {matchedCount}/{totals.item_count}
          </span>
        }
      >
        <div className="space-y-2">
          {matches.map((match, index) => (
            <MatchedItemCard key={`${match.parsed_item.name}-${index}`} match={match} />
          ))}
        </div>
      </CollapsibleSection>

      {/* Optimizations */}
      <CollapsibleSection
        title="Optimization Suggestions"
        badge={
          optimizations.length > 0 ? (
            <span className="px-2 py-0.5 text-xs rounded bg-green-500/10 text-green-400">
              {optimizations.length} found
            </span>
          ) : null
        }
      >
        {optimizations.length > 0 ? (
          <div className="space-y-3">
            {optimizations.map((opt, index) => (
              <OptimizationCard key={`${opt.current_item.name}-${index}`} optimization={opt} />
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <svg
              className="w-12 h-12 mx-auto text-green-500 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-neutral-400">Your gear is already well optimized!</p>
          </div>
        )}
      </CollapsibleSection>

      {/* Missing Essentials */}
      <CollapsibleSection
        title="Missing Essentials"
        badge={
          missing_essentials.length > 0 ? (
            <span className="px-2 py-0.5 text-xs rounded bg-red-500/10 text-red-400">
              {missing_essentials.length} missing
            </span>
          ) : null
        }
      >
        {missing_essentials.length > 0 ? (
          <div className="space-y-2">
            {missing_essentials.map((essential) => (
              <MissingEssentialCard key={essential.category_id} essential={essential} />
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <svg
              className="w-12 h-12 mx-auto text-green-500 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-neutral-400">You have all essentials covered!</p>
          </div>
        )}
      </CollapsibleSection>

      {/* Actions */}
      <div className="space-y-4">
        {savedUrl ? (
          <div className="space-y-3">
            {/* Saved confirmation */}
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <svg className="w-5 h-5 text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm text-green-400">Saved!</span>
              <span className="text-sm text-neutral-400 truncate flex-1">
                {savedUrl.replace('https://', '')}
              </span>
            </div>

            {/* Share buttons - always visible once saved */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <ShareButtons
                  url={savedUrl}
                  title="Gear Shakedown"
                  description={shareDescription}
                  variant="full"
                />
              </div>
              <button
                type="button"
                onClick={onReset}
                className="px-6 py-3 bg-neutral-800 hover:bg-neutral-700 text-white font-medium rounded-lg transition-colors"
              >
                Analyze Another
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              className="flex-1 px-6 py-3 bg-orange-500 hover:bg-orange-400 disabled:bg-neutral-700 text-black font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                    />
                  </svg>
                  <span>Save & Share</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onReset}
              className="px-6 py-3 bg-neutral-800 hover:bg-neutral-700 text-white font-medium rounded-lg transition-colors"
            >
              Analyze Another
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
